import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  Paper,
  Chip,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { Add, Refresh } from '@mui/icons-material';
import { fetchInvoices, createInvoice, updateInvoicePayment, clearInvoice } from '../../services/api';
import InvoiceTable from '../../components/invoices/InvoiceTable';
import { getInvoiceColumns } from './columns';
import { useInvoicePagination } from '../../hooks/useInvoicePagination';

const defaultForm = {
  user_identifier: '',
  subject: '',
  amount: '',
  currency: 'NGN',
  notes: '',
};

const InvoiceManagement = () => {
  const { t } = useTranslation(['invoiceManagement', 'common']);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  
  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [clearModalOpen, setClearModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Form states
  const [form, setForm] = useState(defaultForm);
  const [paymentForm, setPaymentForm] = useState({
    amount_paid: '',
    payment_method: '',
    notes: '',
  });
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // Use pagination hook
  const {
    page,
    rowsPerPage,
    customRowsInput,
    customRowsActive,
    filters,
    handlePageChange,
    handleRowsPerPageChange,
    handleCustomRowsInputChange,
    handleCustomRowsInputBlur,
    handleFilterChange,
    clearFilters,
    getPaginationParams,
  } = useInvoicePagination();
  
  const totalPages = Math.ceil(totalCount / rowsPerPage) || 1;
  
  // Add serial numbers to invoices
  const invoicesWithSerial = invoices.map((invoice, idx) => ({
    ...invoice,
    sn: (page * rowsPerPage) + idx + 1
  }));
  
  // Fetch invoices
  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = getPaginationParams();
      const response = await fetchInvoices(params);
      
      if (response.success) {
        setInvoices(response.data);
        setTotalCount(response.count);
      } else {
        setSnackbar({
          open: true,
          message: response.message || t('messages.failedToLoad'),
          severity: 'error',
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || t('messages.failedToLoad'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [getPaginationParams]);
  
  // Load invoices when pagination or filters change
  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);
  
  // Handle create invoice
  const handleCreateInvoice = async () => {
    try {
      const response = await createInvoice(form);
      // Check if response has invoice data (successful creation)
      if (response && (response.id || response.invoice_number || response.amount)) {
        setSnackbar({
          open: true,
          message: t('messages.createdSuccessfully'),
          severity: 'success',
        });
        setCreateModalOpen(false);
        setForm(defaultForm);
        loadInvoices();
      } else {
        setSnackbar({
          open: true,
          message: response.message || t('messages.failedToCreate'),
          severity: 'error',
        });
      }
    } catch (err) {
      let errorMessage = t('messages.failedToCreate');
      
      if (err.response?.data) {
        // Handle field-specific errors
        if (err.response.data.user_identifier) {
          errorMessage = err.response.data.user_identifier;
        } else if (err.response.data.currency) {
          errorMessage = err.response.data.currency;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (typeof err.response.data === 'object') {
          // Handle multiple field errors
          const fieldErrors = Object.values(err.response.data).flat();
          errorMessage = fieldErrors.join(', ');
        }
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    }
  };
  
  // Handle update payment
  const handleUpdatePayment = async () => {
    try {
      const response = await updateInvoicePayment(selectedInvoice.id, paymentForm);
      if (response.success) {
        setSnackbar({
          open: true,
          message: response.message || t('messages.paymentUpdatedSuccessfully'),
          severity: 'success',
        });
        setPaymentModalOpen(false);
        setPaymentForm({ amount_paid: '', payment_method: '', notes: '' });
        setSelectedInvoice(null);
        loadInvoices();
      } else {
        setSnackbar({
          open: true,
          message: response.message || t('messages.failedToUpdatePayment'),
          severity: 'error',
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || t('messages.failedToUpdatePayment'),
        severity: 'error',
      });
    }
  };

  const handleClearInvoice = async () => {
    try {
      const response = await clearInvoice(selectedInvoice.id);
      if (response.success) {
        setSnackbar({
          open: true,
          message: response.message || t('messages.clearedSuccessfully'),
          severity: 'success',
        });
        setClearModalOpen(false);
        setSelectedInvoice(null);
        loadInvoices();
      } else {
        setSnackbar({
          open: true,
          message: response.message || t('messages.failedToClear'),
          severity: 'error',
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || t('messages.failedToClear'),
        severity: 'error',
      });
    }
  };
  
  // Handle view invoice
  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setViewModalOpen(true);
  };
  
  // Handle update payment modal
  const handleUpdatePaymentModal = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentForm({
      amount_paid: invoice.outstanding || '',
      payment_method: '',
      notes: '',
    });
    setPaymentModalOpen(true);
  };

  const handleClearInvoiceModal = (invoice) => {
    setSelectedInvoice(invoice);
    setClearModalOpen(true);
  };
  
  // Get columns with handlers
  const columns = getInvoiceColumns(handleViewInvoice, handleUpdatePaymentModal, handleClearInvoiceModal, t);
  
  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          {t('title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadInvoices}
            disabled={loading}
            sx={{ minWidth: 'auto' }}
          >
            {t('buttons.refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setCreateModalOpen(true)}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' }, minWidth: 'auto' }}
          >
            {t('buttons.createInvoice')}
          </Button>
        </Box>
      </Box>
      
      {/* Stats */}
      <Card sx={{ mb: 3, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
            {t('summary.totalInvoices')} {totalCount}
          </Typography>
        </CardContent>
      </Card>
      
      {/* Invoice Table */}
      <InvoiceTable
        invoices={invoicesWithSerial}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalPages={totalPages}
        setPage={handlePageChange}
        setRowsPerPage={handleRowsPerPageChange}
        customRowsActive={customRowsActive}
        customRowsInput={customRowsInput}
        setCustomRowsActive={() => {}}
        setCustomRowsInput={() => {}}
        handleCustomRowsInputChange={handleCustomRowsInputChange}
        handleCustomRowsInputBlur={handleCustomRowsInputBlur}
        columns={columns}
        filters={filters}
        onFilterChange={handleFilterChange}
        clearFilters={clearFilters}
      />
      
      {/* Create Invoice Modal */}
      <Dialog open={createModalOpen} onClose={() => setCreateModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('dialogs.create.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label={t('dialogs.create.userLabel')}
              value={form.user_identifier}
              onChange={(e) => setForm({ ...form, user_identifier: e.target.value })}
              required
              fullWidth
              helperText={t('dialogs.create.userHelper')}
            />
            <TextField
              label={t('dialogs.create.subjectLabel')}
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label={t('dialogs.create.amountLabel')}
              type="number"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              required
              fullWidth
              helperText={t('dialogs.create.amountHelper')}
            />
            <TextField
              select
              label={t('dialogs.create.currencyLabel')}
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value })}
              fullWidth
              helperText={t('dialogs.create.currencyHelper')}
            >
              <MenuItem value="NGN">NGN</MenuItem>
              <MenuItem value="CNY">CNY</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
            </TextField>
            <TextField
              label={t('dialogs.create.notesLabel')}
              multiline
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateModalOpen(false)}>{t('buttons.cancel')}</Button>
          <Button onClick={handleCreateInvoice} variant="contained">
            {t('buttons.createInvoice')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* View Invoice Modal */}
      <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{t('dialogs.view.title')} {selectedInvoice?.invoice_number}</DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <Box sx={{ mt: 1 }}>
              {/* Basic Information */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                  {t('dialogs.view.basicInformation')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography><strong>{t('dialogs.view.invoiceNumber')}</strong> {selectedInvoice.invoice_number}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography><strong>{t('dialogs.view.status')}</strong> 
                      <Chip 
                        label={selectedInvoice.status === 'paid' ? t('status.paid') : selectedInvoice.status === 'partially_paid' ? t('status.partiallyPaid') : t('status.unpaid')} 
                        color={selectedInvoice.status === 'paid' ? 'success' : selectedInvoice.status === 'partially_paid' ? 'warning' : 'error'}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography><strong>{t('dialogs.view.subject')}</strong> {selectedInvoice.subject}</Typography>
                  </Grid>
                  {selectedInvoice.notes && (
                    <Grid size={{ xs: 12 }}>
                      <Typography><strong>{t('dialogs.view.notes')}</strong> {selectedInvoice.notes}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* User Information */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                  {t('dialogs.view.userInformation')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography><strong>{t('dialogs.view.userId')}</strong> {selectedInvoice.user?.id}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography><strong>{t('dialogs.view.username')}</strong> {selectedInvoice.user?.username}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography><strong>{t('dialogs.view.email')}</strong> {selectedInvoice.user?.email}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography><strong>{t('dialogs.view.phone')}</strong> {selectedInvoice.user?.phone || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Financial Information */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                  {t('dialogs.view.financialInformation')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography><strong>{t('dialogs.view.totalAmount')}</strong> {selectedInvoice.amount} {selectedInvoice.currency}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography><strong>{t('dialogs.view.amountPaid')}</strong> {selectedInvoice.amount_paid} {selectedInvoice.currency}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography><strong>{t('dialogs.view.outstanding')}</strong> {selectedInvoice.outstanding} {selectedInvoice.currency}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Payment Information */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                  {t('dialogs.view.paymentInformation')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography><strong>{t('dialogs.view.paymentMethod')}</strong> {selectedInvoice.payment_method || 'N/A'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography><strong>{t('dialogs.view.paymentCount')}</strong> {selectedInvoice.payment_count}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Admin Information */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                  {t('dialogs.view.adminInformation')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography><strong>{t('dialogs.view.createdBy')}</strong> {selectedInvoice.operator?.username || 'System'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography><strong>{t('dialogs.view.adminEmail')}</strong> {selectedInvoice.operator?.email || 'N/A'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography><strong>{t('dialogs.view.adminId')}</strong> {selectedInvoice.operator?.id || 'N/A'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography><strong>{t('dialogs.view.ipAddress')}</strong> {selectedInvoice.operator_ip || 'N/A'}</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Timestamps */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                  {t('dialogs.view.timestamps')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography><strong>{t('dialogs.view.createdAt')}</strong> {new Date(selectedInvoice.created_at).toLocaleString()}</Typography>
                  </Grid>
                  {selectedInvoice.paid_at && (
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography><strong>{t('dialogs.view.paidAt')}</strong> {new Date(selectedInvoice.paid_at).toLocaleString()}</Typography>
                    </Grid>
                  )}
                  {selectedInvoice.last_payment_at && (
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography><strong>{t('dialogs.view.lastPayment')}</strong> {new Date(selectedInvoice.last_payment_at).toLocaleString()}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewModalOpen(false)}>{t('buttons.close')}</Button>
        </DialogActions>
      </Dialog>
      
      {/* Update Payment Modal */}
      <Dialog open={paymentModalOpen} onClose={() => setPaymentModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('dialogs.updatePayment.title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label={t('dialogs.updatePayment.amountPaidLabel')}
              type="number"
              value={paymentForm.amount_paid}
              onChange={(e) => setPaymentForm({ ...paymentForm, amount_paid: e.target.value })}
              required
              fullWidth
            />
            <TextField
              select
              label={t('dialogs.updatePayment.paymentMethodLabel')}
              value={paymentForm.payment_method}
              onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
              fullWidth
            >
              <MenuItem value="wallet">{t('paymentMethods.wallet')}</MenuItem>
              <MenuItem value="bank_transfer">{t('paymentMethods.bankTransfer')}</MenuItem>
              <MenuItem value="credit_card">{t('paymentMethods.creditCard')}</MenuItem>
              <MenuItem value="paypal">{t('paymentMethods.paypal')}</MenuItem>
              <MenuItem value="cash">{t('paymentMethods.cash')}</MenuItem>
              <MenuItem value="other">{t('paymentMethods.other')}</MenuItem>
            </TextField>
            <TextField
              label={t('dialogs.updatePayment.notesLabel')}
              multiline
              rows={3}
              value={paymentForm.notes}
              onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentModalOpen(false)}>{t('buttons.cancel')}</Button>
          <Button onClick={handleUpdatePayment} variant="contained">
            {t('buttons.updatePayment')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={clearModalOpen} onClose={() => setClearModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('dialogs.clear.title')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            {t('dialogs.clear.message', { invoiceNumber: selectedInvoice?.invoice_number })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearModalOpen(false)}>{t('buttons.cancel')}</Button>
          <Button onClick={handleClearInvoice} variant="contained" color="error">
            {t('buttons.clearInvoice')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InvoiceManagement;
